import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  createOrganizationAdmin,
  deleteOrganizationAdmin,
  fetchOrganizationAdmins,
  fetchOrganizationsForAdmin,
} from '../../../services/organizationService';

const OrganizationAdminsPage = () => {
  const { token } = useAuth();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [organization, setOrganization] = useState(location.state?.organization || null);
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [adminStatus, setAdminStatus] = useState({ message: '', tone: 'info' });
  const [formValues, setFormValues] = useState({ name: '', email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingAdminId, setRemovingAdminId] = useState('');

  useEffect(() => {
    const loadAdmins = async () => {
      if (!token) {
        setLoadError('You must be signed in as a super admin to view this page.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const [organizationsResponse, adminsResponse] = await Promise.all([
          fetchOrganizationsForAdmin(token),
          fetchOrganizationAdmins(token),
        ]);

        const matchedOrganization = organizationsResponse.organizations?.find(
          (org) => org._id === organizationId,
        );

        setOrganization((previous) => matchedOrganization || previous);

        const filteredAdmins = (adminsResponse.admins || []).filter((admin) => {
          const adminOrganizationId = admin.organization?._id || admin.organization?.id;
          return adminOrganizationId === organizationId;
        });

        setAdmins(filteredAdmins);
        setLoadError(matchedOrganization || location.state?.organization ? '' : 'Organization not found.');
      } catch (err) {
        setLoadError(err.message || 'Unable to load organization admins.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAdmins();
  }, [location.state?.organization, organizationId, token]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setAdminStatus({ message: '', tone: 'info' });
    setFormValues((previous) => ({ ...previous, [name]: value }));
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();

    if (!organizationId) {
      setAdminStatus({ message: 'Organization is required to create an admin.', tone: 'danger' });
      return;
    }

    setIsSubmitting(true);
    setAdminStatus({ message: '', tone: 'info' });

    try {
      const payload = { ...formValues, organizationId };
      const response = await createOrganizationAdmin(payload, token);
      setAdmins((previous) => [response.admin, ...previous]);
      setAdminStatus({ message: 'Organization admin created successfully.', tone: 'success' });
      setFormValues({ name: '', email: '', password: '' });
    } catch (err) {
      setAdminStatus({ message: err.message || 'Unable to create organization admin.', tone: 'danger' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAdmin = async (adminId) => {
    if (!token) {
      setAdminStatus({ message: 'You must be signed in to remove admins.', tone: 'danger' });
      return;
    }

    const confirmed = window.confirm('Remove this organization admin? This action cannot be undone.');

    if (!confirmed) return;

    setRemovingAdminId(adminId);
    setAdminStatus({ message: '', tone: 'info' });

    try {
      await deleteOrganizationAdmin(adminId, token);
      setAdmins((previous) => previous.filter((admin) => (admin._id || admin.id) !== adminId));
      setAdminStatus({ message: 'Organization admin removed successfully.', tone: 'success' });
    } catch (err) {
      setAdminStatus({ message: err.message || 'Unable to remove organization admin.', tone: 'danger' });
    } finally {
      setRemovingAdminId('');
    }
  };

  const createdDate = useMemo(() => {
    if (!organization?.createdAt) return '';
    return new Date(organization.createdAt).toLocaleDateString();
  }, [organization?.createdAt]);

  const approvedAdmins = admins.filter((admin) => admin.approvalStatus === 'approved');
  const adminCount = approvedAdmins.length;
  const isActive = organization?.isActive !== false;

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body d-grid gap-3">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start gap-3">
          <div className="d-grid gap-2">
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2 rounded-pill shadow-sm"
              onClick={() => navigate('/organization')}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                style={{ width: 32, height: 32 }}
                aria-hidden
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.354 2.646a.5.5 0 0 1 0 .708L6.707 8l4.647 4.646a.5.5 0 0 1-.708.708l-5-5a.5.5 0 0 1 0-.708l5-5a.5.5 0 0 1 .708 0Z"
                  />
                </svg>
              </span>
              <span className="fw-semibold">Back to organizations</span>
            </button>
            <div>
              <p className="text-muted small mb-1">Organization admins</p>
              <h1 className="h4 mb-1">{organization?.name || 'Organization directory'}</h1>
              <p className="text-muted mb-0">
                Review organization admins, invite new admins, and keep this organization up to date.
              </p>
            </div>
          </div>

          {organization && (
            <div className="d-flex flex-column align-items-start align-items-lg-end gap-1">
              <span
                className={`badge bg-${
                  isActive ? 'success-subtle text-success-emphasis' : 'secondary-subtle text-secondary-emphasis'
                }`}
              >
                {isActive ? 'Active organization' : 'Archived organization'}
              </span>
              {createdDate && <span className="text-muted small">Created {createdDate}</span>}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="text-center text-muted py-5">Loading organization admins...</div>
        ) : loadError ? (
          <div className="alert alert-danger mb-0" role="alert">
            {loadError}
          </div>
        ) : (
          <div className="d-grid gap-3">
            <div className="card border-0 bg-light-subtle">
              <div className="card-body">
                <form className="d-grid gap-3" onSubmit={handleCreateAdmin}>
                  <div className="d-flex flex-column flex-md-row gap-3">
                    <div className="flex-grow-1">
                      <label className="form-label" htmlFor="adminName">
                        Admin name
                      </label>
                      <input
                        id="adminName"
                        name="name"
                        type="text"
                        className="form-control"
                        placeholder="Priya Organizer"
                        value={formValues.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="flex-grow-1">
                      <label className="form-label" htmlFor="adminEmail">
                        Admin email
                      </label>
                      <input
                        id="adminEmail"
                        name="email"
                        type="email"
                        className="form-control"
                        placeholder="admin@organization.test"
                        value={formValues.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="flex-grow-1">
                      <label className="form-label" htmlFor="adminPassword">
                        Temporary password
                      </label>
                      <input
                        id="adminPassword"
                        name="password"
                        type="password"
                        className="form-control"
                        placeholder="Create a secure password"
                        value={formValues.password}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  {adminStatus.message && (
                    <div
                      className={`alert alert-${
                        adminStatus.tone === 'success'
                          ? 'success'
                          : adminStatus.tone === 'warning'
                            ? 'warning'
                            : 'danger'
                      } py-2 mb-0`}
                      role="alert"
                    >
                      {adminStatus.message}
                    </div>
                  )}

                  <div className="d-flex flex-column flex-sm-row gap-2 align-items-start">
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating admin...' : 'Create organization admin'}
                    </button>
                    <p className="text-muted small mb-0">
                      New admins are approved automatically and can sign in right away.
                    </p>
                  </div>
                </form>
              </div>
            </div>

            <div className="d-grid gap-2">
              <div className="d-flex align-items-center justify-content-between">
                <h2 className="h6 mb-0">Existing admins</h2>
                <span className="badge bg-light text-dark border small">{adminCount} total</span>
              </div>

              {adminCount === 0 ? (
                <div className="text-muted small">No admins assigned yet.</div>
              ) : (
                <ul className="list-unstyled mb-0 d-grid gap-2">
                  {approvedAdmins.map((admin) => {
                    const adminId = admin._id || admin.id;

                    return (
                      <li key={adminId} className="d-flex justify-content-between align-items-center gap-3">
                        <div>
                          <span className="fw-semibold d-block">{admin.name}</span>
                          <span className="text-muted small">{admin.email}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`badge bg-${
                              admin.approvalStatus === 'approved'
                                ? 'success-subtle text-success-emphasis'
                                : 'secondary'
                            }`}
                          >
                            {admin.approvalStatus === 'approved' ? 'Approved' : 'Pending'}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveAdmin(adminId)}
                            disabled={removingAdminId === adminId}
                          >
                            {removingAdminId === adminId ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationAdminsPage;
